/**
 * Course Generation Store
 *
 * Zustand store for managing background course generation state.
 * Persists active jobs across page navigation and browser refresh.
 *
 * @author Tutara Team
 * @since January 2025
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GeneratedCourse, CourseGenerationProgress } from '@/services/course/types';

// =============================================================================
// Types
// =============================================================================

export type CourseGenerationJobStatus =
  | 'queued'
  | 'connecting'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Coarse categorization of why a generation job failed. Used by the UI to
 * decide whether to show a Retry button (transient errors) vs. a sign-in link
 * (UNAUTHORIZED) vs. nothing actionable.
 */
export type CourseGenerationErrorCode =
  | 'CONNECTION'    // SSE closed unexpectedly / backend unreachable
  | 'TIMEOUT'       // inactivity timeout — backend hung
  | 'EXPIRED'       // PAYLOAD_NOT_FOUND / JOB_NOT_FOUND — server forgot the job
  | 'UNAUTHORIZED'  // token expired — not retryable, user must re-auth
  | 'OTHER';        // anything else; retry still allowed (bounded)

export interface CourseGenerationJob {
  id: string;
  courseTopic: string;
  experience: string;
  hobbies: string;
  language: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  status: CourseGenerationJobStatus;
  progress: number;
  currentStep: string;
  errorMessage?: string;
  errorCode?: CourseGenerationErrorCode;
  /** Number of retry attempts the user has already made for this generation. */
  retryCount?: number;
  createdAt: string;
  completedAt?: string;
  result?: GeneratedCourse;
  /** Pre-generation clarifying quiz answers — prompt-only, not persisted server-side. */
  clarifications?: Record<string, number>;
  clarificationsFreeform?: string;
}

export interface CourseGenerationFormData {
  courseTopic: string;
  experience: string;
  hobbies: string;
  language: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  /** Pre-generation clarifying quiz answers (0-3 per question id). */
  clarifications?: Record<string, number>;
  /** Optional 'anything else?' freeform text (capped at 200 chars). */
  clarificationsFreeform?: string;
}

export interface RestoredJobData {
  id: string;
  courseTopic: string;
  experience: string;
  hobbies: string;
  language: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  status: CourseGenerationJobStatus;
  progress: number;
  currentStep: string;
  errorMessage?: string;
  courseId?: string;
  createdAt: number; // timestamp
}

interface CourseGenerationState {
  // State
  jobs: CourseGenerationJob[];

  // Actions
  addJob: (formData: CourseGenerationFormData) => string; // Returns job ID
  updateJobProgress: (jobId: string, progress: CourseGenerationProgress) => void;
  completeJob: (jobId: string, course: GeneratedCourse | { courseId?: string }) => void;
  failJob: (jobId: string, errorMessage: string, errorCode?: CourseGenerationErrorCode) => void;
  cancelJob: (jobId: string) => void;
  removeJob: (jobId: string) => void;
  clearCompletedJobs: () => void;
  restoreJob: (jobData: RestoredJobData) => void; // Restore a job from backend

  // Computed helpers
  getActiveJobs: () => CourseGenerationJob[];
  getJobById: (jobId: string) => CourseGenerationJob | undefined;
  hasActiveJobs: () => boolean;
}

// =============================================================================
// Store
// =============================================================================

export const useCourseGenerationStore = create<CourseGenerationState>()(
  persist(
    (set, get) => ({
      jobs: [],

      addJob: (formData: CourseGenerationFormData) => {
        const jobId = `gen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newJob: CourseGenerationJob = {
          id: jobId,
          courseTopic: formData.courseTopic,
          experience: formData.experience,
          hobbies: formData.hobbies,
          language: formData.language,
          difficulty: formData.difficulty,
          status: 'queued',
          progress: 0,
          currentStep: 'Starting generation...',
          createdAt: new Date().toISOString(),
          clarifications: formData.clarifications,
          clarificationsFreeform: formData.clarificationsFreeform,
        };

        set((state) => ({
          jobs: [...state.jobs, newJob],
        }));

        return jobId;
      },

      updateJobProgress: (jobId: string, progress: CourseGenerationProgress) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  status: progress.status === 'complete' ? 'completed' : 'generating',
                  progress: progress.progress,
                  currentStep: progress.message,
                }
              : job
          ),
        }));
      },

      completeJob: (jobId: string, course: GeneratedCourse | { courseId?: string }) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  status: 'completed' as CourseGenerationJobStatus,
                  progress: 100,
                  currentStep: 'Course generated successfully!',
                  completedAt: new Date().toISOString(),
                  // Only set result if it's a full GeneratedCourse
                  result: 'courseName' in course ? course : job.result,
                }
              : job
          ),
        }));
      },

      failJob: (jobId: string, errorMessage: string, errorCode?: CourseGenerationErrorCode) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  status: 'failed' as CourseGenerationJobStatus,
                  errorMessage,
                  errorCode: errorCode ?? job.errorCode ?? 'OTHER',
                  completedAt: new Date().toISOString(),
                }
              : job
          ),
        }));
      },

      cancelJob: (jobId: string) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  status: 'cancelled' as CourseGenerationJobStatus,
                  currentStep: 'Cancelled by user',
                  completedAt: new Date().toISOString(),
                }
              : job
          ),
        }));
      },

      removeJob: (jobId: string) => {
        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== jobId),
        }));
      },

      clearCompletedJobs: () => {
        set((state) => ({
          jobs: state.jobs.filter(
            (job) => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled'
          ),
        }));
      },

      restoreJob: (jobData: RestoredJobData) => {
        const restoredJob: CourseGenerationJob = {
          id: jobData.id,
          courseTopic: jobData.courseTopic,
          experience: jobData.experience,
          hobbies: jobData.hobbies,
          language: jobData.language,
          difficulty: jobData.difficulty || 'intermediate',
          status: jobData.status,
          progress: jobData.progress,
          currentStep: jobData.currentStep,
          errorMessage: jobData.errorMessage,
          createdAt: new Date(jobData.createdAt).toISOString(),
          completedAt: jobData.status === 'completed' || jobData.status === 'failed' || jobData.status === 'cancelled'
            ? new Date().toISOString()
            : undefined,
        };

        set((state) => {
          // Check if job already exists
          const exists = state.jobs.some((job) => job.id === jobData.id);
          if (exists) {
            return state; // Don't add duplicates
          }

          return {
            jobs: [...state.jobs, restoredJob],
          };
        });
      },

      getActiveJobs: () => {
        const { jobs } = get();
        return jobs.filter(
          (job) =>
            job.status === 'queued' ||
            job.status === 'connecting' ||
            job.status === 'generating'
        );
      },

      getJobById: (jobId: string) => {
        const { jobs } = get();
        return jobs.find((job) => job.id === jobId);
      },

      hasActiveJobs: () => {
        const { jobs } = get();
        return jobs.some(
          (job) =>
            job.status === 'queued' ||
            job.status === 'connecting' ||
            job.status === 'generating'
        );
      },
    }),
    {
      name: 'course-generation-storage',
      partialize: (state) => ({
        // Only persist jobs, keep last 10 to avoid storage bloat
        jobs: state.jobs.slice(-10),
      }),
    }
  )
);

// =============================================================================
// Selectors (for optimized re-renders)
// =============================================================================

export const selectActiveJobs = (state: CourseGenerationState) =>
  state.jobs.filter(
    (job) =>
      job.status === 'queued' ||
      job.status === 'connecting' ||
      job.status === 'generating'
  );

export const selectHasActiveJobs = (state: CourseGenerationState) =>
  state.jobs.some(
    (job) =>
      job.status === 'queued' ||
      job.status === 'connecting' ||
      job.status === 'generating'
  );

export const selectActiveJobsCount = (state: CourseGenerationState) =>
  state.jobs.filter(
    (job) =>
      job.status === 'queued' ||
      job.status === 'connecting' ||
      job.status === 'generating'
  ).length;

export const selectRecentJobs = (state: CourseGenerationState) =>
  state.jobs.slice(-5).reverse();
